import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { setInitialValues as setInitialValuesForPaymentMethods } from '../../ducks/paymentMethods.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  onFetchCustomerCards,
  deleteCard as deleteCardApi,
  onRetrieveCharge,
} from '../../util/api';

// ================ Action types ================ //

export const FETCH_CARDS_REQUEST = 'app/PaymentMethodsPage/FETCH_CARDS_REQUEST';
export const FETCH_CARDS_SUCCESS = 'app/PaymentMethodsPage/FETCH_CARDS_SUCCESS';
export const FETCH_CARDS_ERROR = 'app/PaymentMethodsPage/FETCH_CARDS_ERROR';

export const DELETE_CARD_REQUEST = 'app/PaymentMethodsPage/DELETE_CARD_REQUEST';
export const DELETE_CARD_SUCCESS = 'app/PaymentMethodsPage/DELETE_CARD_SUCCESS';
export const DELETE_CARD_ERROR = 'app/PaymentMethodsPage/DELETE_CARD_ERROR';

export const CHECK_PAYMENT_STATUS_REQUEST = 'app/PaymentMethodsPage/CHECK_PAYMENT_STATUS_REQUEST';
export const CHECK_PAYMENT_STATUS_SUCCESS = 'app/PaymentMethodsPage/CHECK_PAYMENT_STATUS_SUCCESS';
export const CHECK_PAYMENT_STATUS_ERROR = 'app/PaymentMethodsPage/CHECK_PAYMENT_STATUS_ERROR';

// ================ Reducer ================ //

const initialState = {
  cards: null,
  fetchCardsInProgress: false,
  fetchCardsError: null,
  deleteCardInProgress: false,
  deleteCardError: null,
  paymentStatusInProgress: false,
  paymentStatusError: null,
  paymentStatus: null,
};

export default function paymentMethodsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_CARDS_REQUEST:
      return { ...state, fetchCardsInProgress: true, fetchCardsError: null };
    case FETCH_CARDS_SUCCESS:
      return { ...state, fetchCardsInProgress: false, cards: payload };
    case FETCH_CARDS_ERROR:
      return { ...state, fetchCardsInProgress: false, fetchCardsError: payload };

    case DELETE_CARD_REQUEST:
      return { ...state, deleteCardInProgress: true, deleteCardError: null };
    case DELETE_CARD_SUCCESS:
      return {
        ...state,
        deleteCardInProgress: false,
        cards: (state.cards || []).filter(c => c.id !== payload),
      };
    case DELETE_CARD_ERROR:
      return { ...state, deleteCardInProgress: false, deleteCardError: payload };

    case CHECK_PAYMENT_STATUS_REQUEST:
      return { ...state, paymentStatusInProgress: true, paymentStatusError: null, paymentStatus: null };
    case CHECK_PAYMENT_STATUS_SUCCESS:
      return { ...state, paymentStatusInProgress: false, paymentStatus: payload };
    case CHECK_PAYMENT_STATUS_ERROR:
      return { ...state, paymentStatusInProgress: false, paymentStatusError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchCardsRequest = () => ({ type: FETCH_CARDS_REQUEST });
export const fetchCardsSuccess = cards => ({ type: FETCH_CARDS_SUCCESS, payload: cards });
export const fetchCardsError = error => ({ type: FETCH_CARDS_ERROR, payload: error });

export const deleteCardRequest = () => ({ type: DELETE_CARD_REQUEST });
export const deleteCardSuccess = cardId => ({ type: DELETE_CARD_SUCCESS, payload: cardId });
export const deleteCardError = error => ({ type: DELETE_CARD_ERROR, payload: error });

export const checkPaymentStatusRequest = () => ({ type: CHECK_PAYMENT_STATUS_REQUEST });
export const checkPaymentStatusSuccess = status => ({ type: CHECK_PAYMENT_STATUS_SUCCESS, payload: status });
export const checkPaymentStatusError = error => ({ type: CHECK_PAYMENT_STATUS_ERROR, payload: error });

// ================ Thunks ================ //

export const fetchCustomerCards = customerId => (dispatch, getState) => {
  dispatch(fetchCardsRequest());
  return onFetchCustomerCards({ customerId })
    .then(res => {
      const cards = res?.data || [];
      // Filter out duplicate cards based on their id
      const uniqueCards = cards.filter((card, index, self) =>
        index === self.findIndex(c => c.id === card.id)
      );
      dispatch(fetchCardsSuccess(uniqueCards));
      return uniqueCards;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'fetch-customer-cards-failed');
      dispatch(fetchCardsError(error));
      throw error;
    });
};

export const deleteCard = (customerId, cardId) => (dispatch, getState) => {
  dispatch(deleteCardRequest());
  return deleteCardApi({ customer_id: customerId, card_id: cardId })
    .then(() => {
      dispatch(deleteCardSuccess(cardId));
      
      const currentUser = getState().user?.currentUser;
      const oldCards = currentUser?.attributes?.profile?.protectedData?.cards || [];
      const updatedCards = oldCards.filter(c => c.cardId !== cardId);
      
      const currentDefaultCardId = currentUser?.attributes?.profile?.protectedData?.defaultCardId;
      const defaultCardId = currentDefaultCardId === cardId ? '' : currentDefaultCardId;
      
      return dispatch(updateProfile({
        protectedData: {
          cards: updatedCards,
          defaultCardId,
        }
      })).then(() => {
        return dispatch(fetchCustomerCards(customerId));
      });
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'delete-card-failed');
      dispatch(deleteCardError(error));
      throw error;
    });
};

export const checkPaymentStatus = paymentId => (dispatch, getState) => {
  dispatch(checkPaymentStatusRequest());
  return onRetrieveCharge({ charge_id: paymentId })
    .then(res => {
      const { id: tapCustomerId } = res?.customer || {};
      const { id: paymentAgreementId, contract } = res?.payment_agreement || {};
      const cardId = contract?.id;

      if (!paymentAgreementId || !cardId) {
        throw new Error('Payment agreement or card details not found in charge response.');
      }

      const currentUser = getState().user?.currentUser || {};
      const profile = currentUser?.attributes?.profile || {};
      const oldCards = profile.protectedData?.cards || [];
      const currentDefaultCardId = profile.protectedData?.defaultCardId;

      const newCard = {
        paymentAgreementId,
        cardId,
      };

      const cardExists = oldCards.some(card => card.cardId === cardId);
      const updatedCards = cardExists ? oldCards : [...oldCards, newCard];

      // If no default card is set, default to this new card
      const defaultCardId = currentDefaultCardId || cardId;

      return dispatch(
        updateProfile({
          publicData: { customerId: tapCustomerId },
          protectedData: {
            cards: updatedCards,
            defaultCardId,
          },
        })
      ).then(() => {
        dispatch(checkPaymentStatusSuccess(res));
        return dispatch(fetchCustomerCards(tapCustomerId));
      });
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'check-payment-status-failed');
      dispatch(checkPaymentStatusError(error));
      throw error;
    });
};

export const loadData = () => (dispatch, getState) => {
  dispatch(setInitialValuesForPaymentMethods());

  return dispatch(fetchCurrentUser({ callParams: { include: ['stripeCustomer.defaultPaymentMethod'] } }))
    .then(currentUser => {
      const customerId = currentUser?.attributes?.profile?.publicData?.customerId;
      if (customerId) {
        return dispatch(fetchCustomerCards(customerId));
      }
      return null;
    })
    .catch(e => {
      log.error(storableError(e), 'load-payment-methods-page-data-failed');
    });
};
